import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import inventory from "../../../api/inventoryApi";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

const FAKE_DATA = {
    totalGemsDistributed: [
        { name: "Январь", value: 1250 },
        { name: "Февраль", value: 1890 },
        { name: "Март", value: 2100 },
        { name: "Апрель", value: 2450 },
        { name: "Май", value: 2800 },
        { name: "Июнь", value: 3200 },
    ],
    gemsFromPromos: [
        { name: "1 место", gems: 45, promos: 12, position: 1 },
        { name: "2 место", gems: 38, promos: 10, position: 2 },
        { name: "3 место", gems: 32, promos: 8, position: 3 },
        { name: "4 место", gems: 28, promos: 7, position: 4 },
        { name: "5 место", gems: 22, promos: 5, position: 5 },
    ],
    gemOwnership: [
        { name: "1 место", value: 1250, color: "#34d399", position: 1 },
        { name: "2 место", value: 980, color: "#2dd4bf", position: 2 },
        { name: "3 место", value: 750, color: "#22c55e", position: 3 },
        { name: "4 место", value: 520, color: "#10b981", position: 4 },
        { name: "5 место", value: 380, color: "#059669", position: 5 },
        { name: "Другие", value: 120, color: "#047857", position: 0 },
    ],
};

const chartShell =
    "rounded-xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur-sm";

const CustomTooltip = ({ active, payload, label, showGem = false, gemTypes = {} }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const gemType = data?.position ? gemTypes[data.position] : null;
        const gemImage = gemType?.frontImage || gemType?.backImage;

        return (
            <div className="rounded-xl border border-white/12 bg-[#070A12]/95 p-3 shadow-xl backdrop-blur-md">
                <div className="mb-2 flex items-center gap-2">
                    {showGem && gemImage && (
                        <img
                            src={`${IMAGE_BASE_URL}${gemImage}`}
                            alt=""
                            className="h-5 w-5 object-contain"
                            onError={(e) => {
                                e.target.style.display = "none";
                            }}
                        />
                    )}
                    <p className="text-xs font-semibold text-white/90">{label}</p>
                </div>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                        <div
                            className="h-3 w-3 rounded"
                            style={{ backgroundColor: entry.color || "#34d399" }}
                        />
                        <span className="text-white/55">{entry.name}:</span>
                        <span className="font-semibold text-emerald-300/95">{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const CustomXAxisTick = ({ x, y, payload, gemTypes = {} }) => {
    const data = payload.payload;
    const gemType = data?.position ? gemTypes[data.position] : null;
    const gemImage = gemType?.frontImage || gemType?.backImage;

    return (
        <g transform={`translate(${x},${y})`}>
            {gemImage && (
                <foreignObject x={-8} y={-18} width={16} height={16}>
                    <img
                        src={`${IMAGE_BASE_URL}${gemImage}`}
                        alt=""
                        className="h-4 w-4 object-contain"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                </foreignObject>
            )}
            <text x={0} y={0} dy={14} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize={9}>
                {payload.value}
            </text>
        </g>
    );
};

const gridStroke = "rgba(255,255,255,0.08)";
const axisStroke = "rgba(255,255,255,0.35)";

export default function InventoryCharts() {
    const [gemTypes, setGemTypes] = useState({});

    useEffect(() => {
        const fetchGemTypes = async () => {
            try {
                const gems = await inventory.gemCatalogApi.getAllGems();
                const gemsMap = {};
                gems.forEach((gem) => {
                    if (gem.position) {
                        gemsMap[gem.position] = gem;
                    }
                });
                setGemTypes(gemsMap);
            } catch (e) {
                console.error("Ошибка при загрузке типов гемов:", e);
            }
        };

        fetchGemTypes();
    }, []);

    return (
        <div className="mb-0 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className={chartShell}>
                <h3 className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-white/55">
                    Всего кристаллов раздано
                </h3>
                <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={FAKE_DATA.totalGemsDistributed}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                        <XAxis dataKey="name" stroke={axisStroke} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 9 }} />
                        <YAxis stroke={axisStroke} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 9 }} />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: "rgba(7, 10, 18, 0.95)",
                                border: "1px solid rgba(255,255,255,0.12)",
                                borderRadius: "12px",
                                color: "#fff",
                            }}
                        />
                        <Bar dataKey="value" fill="#34d399" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className={chartShell}>
                <h3 className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-white/55">
                    Гемы из промо
                </h3>
                <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={FAKE_DATA.gemsFromPromos}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                        <XAxis
                            dataKey="name"
                            stroke={axisStroke}
                            tick={<CustomXAxisTick gemTypes={gemTypes} />}
                            height={40}
                        />
                        <YAxis stroke={axisStroke} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 9 }} />
                        <Tooltip content={<CustomTooltip showGem gemTypes={gemTypes} />} />
                        <Legend
                            wrapperStyle={{ color: "rgba(255,255,255,0.5)", fontSize: "10px" }}
                            iconSize={10}
                        />
                        <Bar dataKey="gems" fill="#34d399" radius={[4, 4, 0, 0]} name="Гемы" />
                        <Bar dataKey="promos" fill="#2dd4bf" radius={[4, 4, 0, 0]} name="Промо" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className={chartShell}>
                <h3 className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-white/55">
                    Распределение гемов
                </h3>
                <ResponsiveContainer width="100%" height={140}>
                    <PieChart>
                        <Pie
                            data={FAKE_DATA.gemOwnership}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={50}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {FAKE_DATA.gemOwnership.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip showGem gemTypes={gemTypes} />} />
                    </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {FAKE_DATA.gemOwnership.map((entry, index) => {
                        const gemType = entry.position ? gemTypes[entry.position] : null;
                        const gemImage = gemType?.frontImage || gemType?.backImage;
                        return (
                            <div key={index} className="flex items-center gap-1 text-[10px] text-white/45">
                                {gemImage && (
                                    <img
                                        src={`${IMAGE_BASE_URL}${gemImage}`}
                                        alt=""
                                        className="h-3 w-3 object-contain"
                                        onError={(e) => {
                                            e.target.style.display = "none";
                                        }}
                                    />
                                )}
                                <div className="h-2 w-2 rounded" style={{ backgroundColor: entry.color }} />
                                <span>{entry.name}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
